import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import GoToHomePage from './GoToHomePage';
import { Link } from 'react-router-dom';
import { BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { useState, useCallback, useMemo, useEffect } from 'react';

const R_VALUE = 0.0821;
const T_COEF = 273.15;

const sanitize = raw => {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
};

const toNumberIfComplete = str =>
  str && !str.endsWith('.') && Number.isFinite(+str) ? +str : undefined;

const CalculatorModal = ({
                           helmetTitle,
                           title,
                           switcher,
                           formula,
                           descr1,
                           descr2,
                           valuePlaceholder1,
                           valuePlaceholder2,
                           valuePlaceholder3,
                           symbol,
                           legend,
                           unit,
                           accentColor,
                           gasCurrentCalculator
                         }) => {
  const { t } = useTranslation();

  const [raw1, setRaw1] = useState('');
  const [raw2, setRaw2] = useState('');
  const [raw3, setRaw3] = useState('');

  const value1 = toNumberIfComplete(raw1);
  const value2 = toNumberIfComplete(raw2);
  const value3 = toNumberIfComplete(raw3);

  const [answer, setAnswer] = useState();

  const hasAllValues = gasCurrentCalculator
    ? value1 != null && value2 != null && value3 != null
    : value1 != null && value2 != null;

  const calc = useCallback(() => {
    if (!hasAllValues || !value2) return;
    setAnswer(value1 / value2);
  }, [hasAllValues, value1, value2]);

  const gasFormulas = useMemo(
    () => ({
      pressure: () => (value2 * R_VALUE * (value3 + T_COEF)) / value1,
      volume: () => (value2 * R_VALUE * (value3 + T_COEF)) / value1,
      amount: () => (value1 * value2) / (R_VALUE * (value3 + T_COEF)),
      temperature: () => (value1 * value2) / (R_VALUE * value3)
    }),
    [value1, value2, value3]
  );

  const gasCalc = useCallback(() => {
    if (!hasAllValues) return;
    const fn = gasFormulas[gasCurrentCalculator];
    if (typeof fn === 'function') setAnswer(fn());
  }, [hasAllValues, gasFormulas, gasCurrentCalculator]);

  useEffect(() => {
    setRaw1('');
    setRaw2('');
    setRaw3('');
    setAnswer(undefined);
  }, [gasCurrentCalculator]);

  return (
    <>
      <Helmet>
        <title>{`${helmetTitle} – ${t('string.translate.name_ab')} ${new Date().getFullYear()}`}</title>
      </Helmet>

      <section className='calculator-page'>
        <GoToHomePage classNames='go-to-homepage go-to-homepage__full-size' />

        <div className='calculator-page__modal'>
          <div className='calculator-page__modal-wrapper'>
            <div className='calculator-page__header'>
              <Link className='calculator-page__close-button' to='/calculators' />
              <h2 className='calculator-page__title'>{title}</h2>
            </div>

            {switcher}

            <div className='calculator-page__content'>
              <div className='calculator-page__block description-block'>
                <div className='calculator-page__formula-wrapper'>{formula}</div>

                {descr1 && descr2 && (
                  <div className='calculator-page__description'>
                    <h3>{t('calc.translate.mole_calc_instruction_title')}</h3>
                    <p>{descr1}</p>
                    <p>{descr2}</p>
                  </div>
                )}
              </div>

              <div className='calculator-page__block'>
                <div className='calculator-page__inputs'>
                  <label className='calculator-page__input-label'>
                    <span>{valuePlaceholder1}</span>
                    <input
                      value={raw1}
                      inputMode='decimal'
                      onChange={e => setRaw1(sanitize(e.target.value))}
                      placeholder={t('calc.translate.mole_calc_value0_hint')}
                    />
                  </label>

                  <label className='calculator-page__input-label'>
                    <span>{valuePlaceholder2}</span>
                    <input
                      value={raw2}
                      inputMode='decimal'
                      onChange={e => setRaw2(sanitize(e.target.value))}
                      placeholder={t('calc.translate.mole_calc_value0_hint')}
                    />
                  </label>

                  {valuePlaceholder3 && (
                    <label className='calculator-page__input-label'>
                      <span>{valuePlaceholder3}</span>
                      <input
                        value={raw3}
                        inputMode='decimal'
                        onChange={e => setRaw3(sanitize(e.target.value))}
                        placeholder={t('calc.translate.mole_calc_value0_hint')}
                      />
                    </label>
                  )}
                </div>
                {answer != null && (
                  <div className='calculator-page__answer-wrapper'>
                    <h4 style={{ '--before-color': accentColor }}>
                      {t('calc.translate.concentrat_calc_answer_title')}
                    </h4>
                    <p>{legend}</p>

                    {!gasCurrentCalculator ? (
                      <BlockMath
                        math={`${symbol} = \\frac{${value1}}{${value2}} = ${answer.toFixed(
                          4
                        )}\\,\\text{${unit ?? ''}}`}
                      />
                    ) : (
                      <div className='calculator-page__answer'>
                        {answer.toFixed(4)} {unit}
                      </div>
                    )}
                  </div>
                )}
                <button
                  className='calculator-page__calc-button'
                  onClick={gasCurrentCalculator ? gasCalc : calc}
                  disabled={!hasAllValues}
                >
                  {hasAllValues
                    ? t('calc.translate.mole_calc_answer_btn')
                    : t('calc.translate.calc_answer_btn_error')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CalculatorModal;